import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border px-2.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] [&>svg]:pointer-events-none [&>svg]:size-3 focus-visible:ring-[3px] focus-visible:ring-primary/30 aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-neutral-line bg-neutral-soft text-neutral-soft-foreground',
        success:
          'border-success-line bg-success-soft text-success-soft-foreground',
        warning:
          'border-warning-line bg-warning-soft text-warning-soft-foreground',
        destructive:
          'border-danger-line bg-danger-soft text-danger-soft-foreground',
        info: 'border-info-line bg-info-soft text-info-soft-foreground',
        ink: 'border-transparent bg-ink text-primary',
        outline:
          'border-border bg-card text-foreground-secondary [a&]:hover:bg-sunken',
        successSubtle:
          'border-transparent bg-primary-soft text-foreground',
        primaryOutline:
          'border-transparent bg-primary-soft text-foreground',
        successOutline:
          'border-success-line bg-success-soft text-success-soft-foreground',
        warningOutline:
          'border-warning-line bg-warning-soft text-warning-soft-foreground',
        destructiveOutline:
          'border-danger-line bg-danger-soft text-danger-soft-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
